import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import VisitorTracker from '../../utils/VisitorTracker';
import SecureTracker from '../../utils/SecureTracker';

const EmailCaptureOverlay = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, ${({ theme }) => theme.card} 0%, ${({ theme }) => theme.card_light} 100%);
  border: 1px solid rgba(133, 76, 230, 0.3);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(133, 76, 230, 0.2);
  backdrop-filter: blur(10px);
  z-index: 1000;
  max-width: 320px;
  transform: ${({ show }) => show ? 'translateY(0)' : 'translateY(120%)'};
  opacity: ${({ show }) => show ? '1' : '0'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    bottom: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 18px;
  cursor: pointer;
  opacity: 0.6;
  
  &:hover {
    opacity: 1;
  }
`;


const Subtitle = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.text_primary};
  opacity: 0.9;
  margin-bottom: 12px;
  line-height: 1.5;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(133, 76, 230, 0.3);
  border-radius: 8px;
  background: rgba(133, 76, 230, 0.05);
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  margin-bottom: 10px;
  
  &::placeholder {
    color: ${({ theme }) => theme.text_primary};
    opacity: 0.6;
  }
  
  &:focus {
    outline: none;
    border-color: #854CE6;
    box-shadow: 0 0 0 2px rgba(133, 76, 230, 0.1);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 10px;
  background: linear-gradient(135deg, #854CE6, #9d5ce8);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(133, 76, 230, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmailCapture = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Show after 15 seconds if not already shown in this session
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem('email_capture_shown')) {
        setShow(true);
        sessionStorage.setItem('email_capture_shown', 'true');
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    
    try {
      // Get visitor info and use secure tracker
      const visitorInfo = await VisitorTracker.getVisitorInfo();
      await SecureTracker.trackEmailCapture(email, visitorInfo);

      setShow(false);
      sessionStorage.setItem('email_provided', email);
      
    } catch (error) {
      console.error('Failed to send email capture:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    setShow(false);
    
    // Track that user dismissed the email capture
    try {
      const visitorInfo = await VisitorTracker.getVisitorInfo();
      
      // Use secure tracker with new template
      await SecureTracker.trackModalDismiss(visitorInfo);

      console.log('📊 Modal dismissal tracked and sent!');
      
    } catch (error) {
      console.error('❌ Failed to send dismissal analytics:', error);
    }
  };

  if (!show) return null;

  return (
    <EmailCaptureOverlay show={show}>
      <CloseButton onClick={handleClose}>×</CloseButton>
      <Subtitle>
        Enjoying my website? 😄 Drop your email to connect or get updates! 
        
        For detailed chats, hit me up via the contact form below! 🤙
      </Subtitle>
      <form onSubmit={handleSubmit}>
        <EmailInput
          type="email"
          placeholder="your.email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Connecting...' : "Let's connect! 💬"}
        </SubmitButton>
      </form>
    </EmailCaptureOverlay>
  );
};

export default EmailCapture;
